import React, { useState } from 'react';
import { Box, Typography, Tabs, Tab, Grid, Paper, Divider, Chip } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

// --- DONNÉES SIMULÉES (MOCK DATA) ---
const mockRawMaterials = [
    { name: 'Huile de Palme', risk: 85, volume: 1200, percentTraced: 45 },
    { name: 'Mica', risk: 92, volume: 300, percentTraced: 20 },
    { name: 'Karité', risk: 60, volume: 800, percentTraced: 80 },
    { name: 'Vanille', risk: 75, volume: 150, percentTraced: 65 },
    { name: 'Cacao', risk: 70, volume: 500, percentTraced: 50 },
];

const mockSupplierAuditStatus = [
    { name: 'Valid Audit (< 12 months)', value: 45 },
    { name: 'Expired Audit', value: 25 },
    { name: 'No Audit', value: 30 },
];

const mockAlertsTrend = [
    { month: 'Jan', alerts: 2, closed: 1 },
    { month: 'Feb', alerts: 5, closed: 3 },
    { month: 'Mar', alerts: 3, closed: 4 },
    { month: 'Apr', alerts: 8, closed: 5 },
    { month: 'May', alerts: 4, closed: 6 },
    { month: 'Jun', alerts: 6, closed: 5 },
];

const mockEnvironment = [
    { name: 'Deforestation Free', value: 65 },
    { name: 'Water Usage Plan', value: 40 },
    { name: 'Biodiversity Action', value: 30 },
];

// --- SOUS-COMPOSANTS ---

const RawMaterialsTab = () => (
    <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>Top 5 Matières Premières à Risque (Hotspots)</Typography>
        <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
                <Paper sx={{ p: 2, height: 400 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={mockRawMaterials}
                            layout="vertical"
                            margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" domain={[0, 100]} />
                            <YAxis dataKey="name" type="category" width={100} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="risk" name="Score de Risque" fill="#FF8042" />
                            <Bar dataKey="percentTraced" name="% Tracé (Rang 2+)" fill="#00C49F" />
                        </BarChart>
                    </ResponsiveContainer>
                </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2, height: 400, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography variant="h4" align="center" color="primary">45%</Typography>
                    <Typography variant="body1" align="center" gutterBottom>Moyenne traçabilité Rang 2+</Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h4" align="center" color="error">3</Typography>
                    <Typography variant="body1" align="center">Matières Critiques sans Audit</Typography>
                </Paper>
            </Grid>
        </Grid>
    </Box>
);

const SuppliersTab = () => (
    <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>Couverture Audit & Conformité Fournisseurs</Typography>
        <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: 350 }}>
                    <Typography align="center" variant="subtitle1">Statut des Audits (Fournisseurs Critiques)</Typography>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={mockSupplierAuditStatus}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={80}
                                fill="#8884d8"
                                paddingAngle={5}
                                dataKey="value"
                            >
                                {mockSupplierAuditStatus.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: 350 }}>
                    <Typography variant="h6" gutterBottom>Plans d'Actions (CAPA)</Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Chip label="Total: 42" />
                        <Chip label="Ouverts: 12" color="warning" />
                        <Chip label="En Retard: 3" color="error" />
                    </Box>
                    <Typography variant="body2">
                        Le délai moyen de clôture des non-conformités majeures est de 45 jours.
                        Les récidives sont surveillées sur les audits H&S.
                    </Typography>
                </Paper>
            </Grid>
        </Grid>
    </Box>
);

const AlertsTab = () => (
    <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>Gestion des Alertes & Incidents</Typography>
        <Paper sx={{ p: 2, height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart
                    data={mockAlertsTrend}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="alerts" name="Alertes Reçues" stroke="#8884d8" activeDot={{ r: 8 }} />
                    <Line type="monotone" dataKey="closed" name="Alertes Clôturées" stroke="#82ca9d" />
                </LineChart>
            </ResponsiveContainer>
        </Paper>
    </Box>
);

const EnvironmentTab = () => (
    <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>Environnement Amont (Biodiversité / Eau / Déforestation)</Typography>
        <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: 300 }}>
                    <Typography variant="subtitle1" gutterBottom>% Conformité par Thématique</Typography>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={mockEnvironment}
                            layout="vertical"
                            margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" domain={[0, 100]} />
                            <YAxis dataKey="name" type="category" width={120} />
                            <Tooltip />
                            <Bar dataKey="value" fill="#82ca9d" name="% Conformité" />
                        </BarChart>
                    </ResponsiveContainer>
                </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, height: 300 }}>
                    <Typography variant="h6">Indicateurs Clés</Typography>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body1">Sites à risque hydrique élevé : <strong>8</strong></Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body1">Fournisseurs sans politique Déforestation : <strong>12</strong></Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body1">Incidents pollution majeurs (12 mois) : <strong>0</strong></Typography>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    </Box>
);


const CosmeticsDashboard = () => {
    const [tabIndex, setTabIndex] = useState(0);

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabIndex} onChange={handleTabChange} >
                    <Tab label="Matières Premières" />
                    <Tab label="Fournisseurs" />
                    <Tab label="Alertes" />
                    <Tab label="Environnement" />
                </Tabs>
            </Box>
            <Box sx={{ p: 2 }}>
                {tabIndex === 0 && <RawMaterialsTab />}
                {tabIndex === 1 && <SuppliersTab />}
                {tabIndex === 2 && <AlertsTab />}
                {tabIndex === 3 && <EnvironmentTab />}
            </Box>
        </Box>
    );
};

export default CosmeticsDashboard;
